import * as z from 'zod';

// The merchant's legacy chats (courier/support threads about orders) — the
// old panel's drawer, served by `GET /api/v1/chats…` on the legacy backend.
// This is NOT `entities/support-chat`: that slice is the Figma-designed inbox
// awaiting its own (future) messaging service, while this one mirrors the
// `/api/v1/chats` wire format exactly.

/** The person on the other end of a chat (courier, client, or agent). */
export const merchantChatUserSchema = z.object({
  id: z.number().optional(),
  role: z.string().optional(),
  first_name: z.string().optional(),
  last_name: z.string().optional(),
});
export type MerchantChatUser = z.infer<typeof merchantChatUserSchema>;

/** Thread lifecycle: 1 open, 2 acquired, 3 closed-resolved, 4 closed-unresolved. */
export const MERCHANT_CHAT_STATUS = {
  open: 1,
  acquired: 2,
  closedResolved: 3,
  closedUnresolved: 4,
} as const;

export const merchantChatLastMessageSchema = z.object({
  message: z.string().nullable(),
  sent_at: z.string().nullable(),
  outgoing: z.boolean().nullable(),
  seen: z.boolean().nullable(),
});
export type MerchantChatLastMessage = z.infer<typeof merchantChatLastMessageSchema>;

/** One conversation row (`ChatWithLastMessage` on the wire). */
export const merchantChatSchema = z.object({
  id: z.number(),
  subject: z.string().nullish(),
  user: merchantChatUserSchema,
  order_id: z.number().nullable(),
  display_id: z.number().nullable(),
  status: z.number().optional(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  responsible: merchantChatUserSchema.nullish(),
  new_user_id: z.number().optional(),
  confirmed: z.boolean().nullish(),
  last_message: merchantChatLastMessageSchema.nullish(),
});
export type MerchantChat = z.infer<typeof merchantChatSchema>;

export const merchantChatListSchema = z.object({
  data: z.array(merchantChatSchema),
  count: z.number(),
  current_page: z.number(),
});
export type MerchantChatList = z.infer<typeof merchantChatListSchema>;

/** One message (`ChatMessage` on the wire). Either text or an attachment. */
export const merchantChatMessageSchema = z.object({
  id: z.number(),
  message: z.string(),
  outgoing: z.boolean(),
  seen: z.boolean(),
  created_at: z.string(),
  files: z.array(z.string()).nullish(),
});
export type MerchantChatMessage = z.infer<typeof merchantChatMessageSchema>;

export const merchantChatThreadSchema = z.object({
  data: z.array(merchantChatMessageSchema),
  count: z.number(),
});
export type MerchantChatThread = z.infer<typeof merchantChatThreadSchema>;

/**
 * A live `chat_message` push (`ChatMessageDto` on the wire). Same shape as a
 * REST message plus its `chat_id`, minus `seen` — the socket hook maps it onto
 * `merchantChatMessageSchema` (unseen) before appending it to the thread cache.
 */
export const merchantChatSocketMessageSchema = z.object({
  id: z.number(),
  chat_id: z.number(),
  message: z.string(),
  outgoing: z.boolean(),
  created_at: z.string(),
  files: z.array(z.string()).nullish(),
});
export type MerchantChatSocketMessage = z.infer<typeof merchantChatSocketMessageSchema>;
